import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  aggregateMobilityRows,
  getSesMetrics,
  matchMobilityByAddress,
  normalizeMobilityRows,
} from "../../utils/mobility";
import { trNormalize } from "../../utils/text";
import { normalizeCoordinateString } from "../../utils/coordinates";

const integer = (value) => Math.round(Number(value) || 0);
const formatNumber = (value) => integer(value).toLocaleString("tr-TR", { maximumFractionDigits: 0 });
const formatPercent = (value) => `${((Number(value) || 0) * 100).toFixed(1)}%`;
const PIE_COLORS = ["#60a5fa", "#34d399", "#fbbf24", "#f472b6", "#a78bfa", "#fb7185"];

const dedupeKeyForMatch = (site, rows) => {
  const firstRow = rows?.[0] || {};
  const rowLatitude = normalizeCoordinateString(firstRow.latitude, "lat");
  const rowLongitude = normalizeCoordinateString(firstRow.longitude, "lng");
  if (rowLatitude && rowLongitude) return `mobility-coordinate:${rowLatitude}|${rowLongitude}`;

  const siteLatitude = normalizeCoordinateString(site.latitude, "lat");
  const siteLongitude = normalizeCoordinateString(site.longitude, "lng");
  if (siteLatitude && siteLongitude) return `site-coordinate:${siteLatitude}|${siteLongitude}`;

  return `address:${trNormalize(site.name || site.address)}`;
};

const mergeUnitTypes = (...unitTypes) =>
  Array.from(new Set(unitTypes.flatMap((value) => String(value || "").split(" + ")).map((value) => value.trim()).filter(Boolean))).join(" + ");

export default function MobilityMatchedList({ plan, datasets }) {
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(20);

  const mobilityRows = useMemo(() => normalizeMobilityRows(datasets || []), [datasets]);
  const matchedSites = useMemo(() => {
    const addresses = Array.isArray(plan?.addresses) ? plan.addresses : [];
    const deduped = new Map();
    addresses.forEach((address, index) => {
      const site = {
        id: address._id || address.id || index,
        address: address.address,
        name: address.name || address.address,
        unitType: address.unitType || address.unit || "",
        city: address.city || "",
        // Haritadaki eşleşmeyle aynı onarım adımı: ham koordinat virgüllü/ölçek
        // sorunlu olabiliyor, normalizeCoordinateString bunu standart forma çeviriyor.
        latitude: normalizeCoordinateString(address.latitude, "lat"),
        longitude: normalizeCoordinateString(address.longitude, "lng"),
      };
      const rows = matchMobilityByAddress(site, mobilityRows);
      if (!rows.length) return;

      const key = dedupeKeyForMatch(site, rows);
      const existing = deduped.get(key);
      if (existing) {
        existing.site.unitType = mergeUnitTypes(existing.site.unitType, site.unitType);
        existing.site.duplicateCount = (existing.site.duplicateCount || 1) + 1;
        return;
      }

      deduped.set(key, {
        site: { ...site, duplicateCount: 1 },
        rows,
        aggregate: aggregateMobilityRows(rows),
      });
    });
    return Array.from(deduped.values());
  }, [plan, mobilityRows]);

  const filteredSites = useMemo(() => {
    const normalizedQuery = trNormalize(query);
    if (!normalizedQuery) return matchedSites;
    return matchedSites.filter(({ site }) => trNormalize(site.name || site.address).includes(normalizedQuery));
  }, [matchedSites, query]);

  const totalAggregate = useMemo(
    () => aggregateMobilityRows(matchedSites.flatMap(({ rows }) => rows)),
    [matchedSites]
  );

  const exportExcel = () => {
    const detailColumns = Array.from(new Set(filteredSites.flatMap(({ aggregate }) =>
      Object.keys(aggregate.shares || {}).filter((name) => !name.startsWith("__EMPTY"))
    ))).sort((first, second) => first.localeCompare(second, "tr", { sensitivity: "base" }));
    const fixedColumns = [
      "Lokasyon", "Şehir", "Ünite Tipi", "Toplam Haftalık Erişim",
      "SES A Haftalık Erişim", "SES B Haftalık Erişim", "SES C Haftalık Erişim", "SES D-E Haftalık Erişim",
    ];
    const rows = filteredSites.map(({ site, aggregate }) => {
      const metrics = Object.fromEntries(getSesMetrics(aggregate).map((metric) => [metric.key, metric]));
      const row = {
        "Lokasyon": site.name || site.address,
        "Şehir": site.city,
        "Ünite Tipi": site.unitType,
        "Toplam Haftalık Erişim": integer(aggregate.totalUsers),
        "SES A Haftalık Erişim": integer(metrics.A.reach),
        "SES B Haftalık Erişim": integer(metrics.B.reach),
        "SES C Haftalık Erişim": integer(metrics.C.reach),
        "SES D-E Haftalık Erişim": integer(metrics.DE.reach),
      };
      detailColumns.forEach((column) => { row[column] = Number(aggregate.shares?.[column]) || 0; });
      return row;
    });
    const headers = [...fixedColumns, ...detailColumns];
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    detailColumns.forEach((_, detailIndex) => {
      rows.forEach((__, rowIndex) => {
        const cell = worksheet[XLSX.utils.encode_cell({ c: fixedColumns.length + detailIndex, r: rowIndex + 1 })];
        if (cell) cell.z = "0.0%";
      });
    });
    worksheet["!autofilter"] = { ref: worksheet["!ref"] };
    worksheet["!cols"] = headers.map((header, index) => ({
      wch: index === 0 ? 48 : index < fixedColumns.length ? 18 : Math.min(Math.max(header.length + 2, 22), 52),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Mobility Eşleşmeleri");
    XLSX.writeFile(workbook, `mobility-eslesmeleri-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (!plan || !mobilityRows.length) return null;

  return (
    <section className="mt-4 space-y-4">
      {matchedSites.length > 0 && <MobilityTotalResultsV2 aggregate={totalAggregate} matchedCount={matchedSites.length} />}

      <div className="card p-4 space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">Eşleşen Lokasyonlar</h3>
          <div className="text-xs text-slate-500 mt-1">{filteredSites.length} eşleşen lokasyon</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="search"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setVisibleCount(20); }}
            placeholder="Lokasyon ismi ile ara"
            className="h-10 min-w-[260px] rounded border border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-500"
          />
          <button
            type="button"
            onClick={exportExcel}
            disabled={!filteredSites.length}
            className="h-10 px-4 rounded border bg-white text-black text-sm font-medium hover:bg-gray-400 disabled:opacity-40"
          >
            Excel İndir
          </button>
        </div>
      </div>

      {filteredSites.length === 0 ? (
        <div className="rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
          Eşleşen lokasyon bulunmadı.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="hidden md:grid md:grid-cols-[minmax(260px,1fr)_190px_140px_minmax(280px,380px)_32px] gap-3 px-4 text-xs font-extrabold uppercase tracking-wide text-slate-400">
            <div>Lokasyon</div>
            <div>Ünite</div>
            <div>Haftalık Erişim</div>
            <div>SES Dağılımı</div>
            <div aria-hidden="true" />
          </div>
          {filteredSites.slice(0, visibleCount).map(({ site, rows, aggregate }, index) => (
            <CompactMobilityLocationRow key={site.id} site={site} rows={rows} aggregate={aggregate} index={index} />
          ))}
        </div>
      )}

      {visibleCount < filteredSites.length && (
        <button type="button" onClick={() => setVisibleCount((count) => count + 20)} className="w-full h-10 rounded border border-blue-200 text-sm text-blue-700 hover:bg-blue-50">
          Daha Fazla Göster ({filteredSites.length - visibleCount})
        </button>
      )}
      </div>
    </section>
  );
}

function MobilityTotalResultsV2({ aggregate, matchedCount }) {
  const share = (label) => getShareValue(aggregate, label);
  const operatorData = [
    { label: "Turkcell", value: share("Kullanılan Mobil Operatör Dağılımı % Tcell") },
    { label: "Vodafone", value: share("Kullanılan Mobil Operatör Dağılımı % Vodafone") },
    { label: "Türk Telekom", value: share("Kullanılan Mobil Operatör Dağılımı % TurkTelekom") },
  ].sort((first, second) => second.value - first.value);
  const phoneData = withOtherShare([
    { label: "Apple", value: share("Sahip Olunan Telefon Markası Dağılımı Apple") },
    { label: "Samsung", value: share("Sahip Olunan Telefon Markası Dağılımı Samsung") },
    { label: "Xiaomi", value: share("Sahip Olunan Telefon Markası Dağılımı Xiaomi") },
  ]);
  const sesData = [
    { label: "SES A", value: share("Sosyo Ekonomik Segment Dağılımı A") },
    { label: "SES B", value: share("Sosyo Ekonomik Segment Dağılımı B") },
    { label: "SES C", value: share("Sosyo Ekonomik Segment Dağılımı C") },
    { label: "SES D-E", value: share("Sosyo Ekonomik Segment Dağılımı DE") },
  ];
  const extraShareMetrics = getExtraTotalShareMetrics(aggregate);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <h3 className="text-lg font-extrabold text-slate-950">Total Sonuçlar — Eşleşen Adresler</h3>
        {/* <p className="text-xs font-medium text-slate-400">
          Tekil kullanıcı değerleri haftalıktır · Kaynak: ortalama haftalık tekil kişi (Mart–Ağustos 2025)
        </p> */}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_1.1fr_1.2fr_1.2fr]">
        <div className="xl:border-r xl:border-slate-100 xl:pr-6">
          <div className="text-xs font-extrabold text-slate-500">Toplam Haftalık Erişim</div>
          <div className="mt-1 text-5xl font-black tracking-tight text-slate-950">{formatNumber(aggregate.totalUsers)}</div>
          {/* <div className="mt-2 text-sm font-medium text-slate-400">{matchedCount} eşleşen adresin toplamı</div> */}
          <div className="mt-8 space-y-5">
            {extraShareMetrics.map((metric) => (
              <TotalInlineMetric key={metric.label} label={metric.label} value={formatPercent(metric.value)} />
            ))}
          </div>
        </div>

        <div>
          <div className="mb-4 text-sm font-extrabold text-slate-800">Mobil Operatör</div>
          <div className="divide-y divide-slate-100">
            {operatorData.map((item, index) => (
              <div key={item.label} className="flex items-center gap-3 py-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-extrabold text-slate-500">{index + 1}</span>
                <span className="flex-1 text-sm font-medium text-slate-600">{item.label}</span>
                <span className="text-3xl font-black text-slate-700">{formatPercent(item.value)}</span>
              </div>
            ))}
          </div>
          {/* {operatorGap > 0 && (
            <div className="mt-3 text-xs font-medium text-slate-400">
              Lider ile 2. arasında <span className="font-extrabold text-slate-700">+{formatPercent(operatorGap).replace("%", "")} puan</span> fark
            </div>
          )} */}
        </div>

        <TotalDistributionBlock
          title="Telefon Markası"
          data={phoneData}
          colors={["#0f766e", "#2d8f7f", "#88c5ba", "#cfe8e3"]}
        />

        <TotalDistributionBlock
          title="Sosyo-Ekonomik Segment"
          data={sesData}
          colors={["#0f3f66", "#2f6690", "#9bc2de", "#d5e5f2"]}
        />
      </div>
    </div>
  );
}

function TotalInlineMetric({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-4 last:border-b-0">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className="text-xl font-black text-slate-950">{value}</span>
    </div>
  );
}

function getExtraTotalShareMetrics(aggregate) {
  return Object.entries(aggregate?.shares || {})
    .filter(([name]) => {
      if (!name || name.startsWith("__EMPTY")) return false;
      const normalized = trNormalize(name);
      return !(
        normalized === "longitude" ||
        normalized === "longtitude" ||
        normalized === "latitude" ||
        normalized.includes("koordinat") ||
        normalized.includes("coordinate") ||
        normalized.includes("mobil operator") ||
        normalized.includes("mobil operat") ||
        normalized.includes("telefon markasi") ||
        normalized.includes("telefon") ||
        normalized.includes("sosyo ekonomik") ||
        normalized.includes("segment dagilimi")
      );
    })
    .map(([label, value]) => ({ label: formatExtraMetricLabel(label), value: Number(value) || 0 }))
    .sort((first, second) => second.value - first.value);
}

function formatExtraMetricLabel(label) {
  const normalized = trNormalize(label);
  if (
    normalized.includes("a segment shopping mall") ||
    normalized.includes("a segment alisveris merkezi")
  ) {
    return "A Segment Alışveriş Merkezi Aylık Ortalama Ziyaret Sayısı";
  }
  return label;
}

function TotalDistributionBlock({ title, data, footer, colors }) {
  const visibleData = data.map((item, index) => ({
    ...item,
    color: colors[index % colors.length],
    value: Number(item.value) || 0,
  })).filter((item) => item.value > 0);

  return (
    <div>
      <div className="mb-4 text-sm font-extrabold text-slate-800">{title}</div>
      <div className="grid grid-cols-2 gap-3">
        {visibleData.map((item) => (
          <div key={item.label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
              {item.label}
            </div>
            <div className="mt-2 text-2xl font-black text-slate-950">{formatPercent(item.value)}</div>
          </div>
        ))}
      </div>
      <StackedShareBar data={visibleData} />
      {footer && <div className="mt-3 text-xs font-medium text-slate-400">{footer}</div>}
    </div>
  );
}

function DetailDistributionList({ title, data, colors, logoType }) {
  const visibleData = withOtherShare(data)
    .map((item, index) => ({
      ...item,
      color: colors[index % colors.length],
      value: Number(item.value) || 0,
    }))
    .filter((item) => item.value > 0);

  return (
    <div>
      <div className="mb-5 text-xs font-extrabold uppercase tracking-wide text-slate-500">{title}</div>
      <div className="space-y-1">
        {visibleData.map((item) => (
          <div key={item.label} className="grid grid-cols-[28px_minmax(0,1fr)_72px] items-center gap-2 border-b border-slate-100 py-2 last:border-b-0">
            <BrandBadge label={item.label} color={item.color} type={logoType} />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-600">{item.label}</span>
            <span className="text-right text-sm font-black tabular-nums text-slate-950">{formatPercent(item.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const BRAND_LOGOS = {
  apple: "https://cdn-icons-png.flaticon.com/512/0/747.png",
  turkcell: "https://ffo3gv1cf3ir.merlincdn.net/SiteAssets/Hakkimizda/genel-bakis/logolarimiz/AMBLEM_SARI.png?20260707_03-135355",
  xiaomi: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT47GS9rEvDCTTiFu84Nvzthyjx5-WCMRwoZkQ2mrxBaQ&s",
  samsung: "https://images.samsung.com/is/image/samsung/assets/tr/about-us/brand/logo/mo/360_197_1.png?$720_N_PNG$",
  turkTelekom: "https://companieslogo.com/img/orig/TTKOM.IS-802a5da1.png?t=1720244494",
  vodafone: "https://i.pinimg.com/736x/f8/6f/1c/f86f1c6a00687b94cb43c0c12455522b.jpg",
};

function logoUrlForLabel(label) {
  const normalized = trNormalize(label);
  if (normalized.includes("apple")) return BRAND_LOGOS.apple;
  if (normalized.includes("turkcell")) return BRAND_LOGOS.turkcell;
  if (normalized.includes("xiaomi")) return BRAND_LOGOS.xiaomi;
  if (normalized.includes("samsung")) return BRAND_LOGOS.samsung;
  if (normalized.includes("turk telekom")) return BRAND_LOGOS.turkTelekom;
  if (normalized.includes("vodafone")) return BRAND_LOGOS.vodafone;
  return "";
}

function BrandBadge({ label, color, type }) {
  const normalized = trNormalize(label);
  const isLight = ["#e8eef5", "#e5e7eb", "#f8fafc", "#d5e5f2", "#cfe4df"].includes(String(color).toLowerCase());
  const baseClass = "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10px] font-black shadow-sm";
  const style = {
    backgroundColor: color,
    color: isLight ? "#334155" : "#ffffff",
    border: isLight ? "1px solid #e2e8f0" : "1px solid transparent",
  };

  if (type === "ses") {
    return <span className="h-3.5 w-3.5 shrink-0 rounded" style={{ backgroundColor: color }} />;
  }

  const logoUrl = logoUrlForLabel(label);
  if (logoUrl) {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-100 bg-white shadow-sm">
        <img src={logoUrl} alt={`${label} logo`} className="h-6 w-6 object-contain" loading="lazy" referrerPolicy="no-referrer" />
      </span>
    );
  }

  if (normalized.includes("turkcell")) return <span className={`${baseClass} rounded-full`} style={style}>TC</span>;
  if (normalized.includes("vodafone")) return <span className={`${baseClass} rounded-full`} style={style}>V</span>;
  if (normalized.includes("turk telekom")) return <span className={baseClass} style={style}>TT</span>;
  if (normalized.includes("apple")) return <span className={baseClass} style={style}></span>;
  if (normalized.includes("samsung")) return <span className={baseClass} style={style}>S</span>;
  if (normalized.includes("xiaomi")) return <span className={baseClass} style={style}>mi</span>;
  if (normalized.includes("diger") || normalized.includes("diğer")) return <span className={baseClass} style={style}>?</span>;

  return <span className={baseClass} style={style}>{String(label || "?").slice(0, 2).toUpperCase()}</span>;
}

function StackedShareBar({ data }) {
  const total = data.reduce((sum, item) => sum + item.value, 0) || 1;
  return (
    <div className="mt-4">
      <div className="mb-2 grid gap-1" style={{ gridTemplateColumns: `repeat(${Math.max(data.length, 1)}, minmax(0, 1fr))` }}>
        {data.map((item) => (
          <div key={`${item.label}-label`} className="min-w-0 text-center">
            <div className="truncate text-[10px] font-semibold text-slate-500">{item.label}</div>
            <div className="mt-0.5 text-[11px] font-extrabold tabular-nums text-slate-800">{formatPercent(item.value)}</div>
          </div>
        ))}
      </div>
      <div className="flex h-6 overflow-hidden rounded-full bg-slate-100">
        {data.map((item) => (
          <span
            key={item.label}
            className="block"
            style={{
              width: `${Math.max((item.value / total) * 100, item.value > 0 ? 2 : 0)}%`,
              backgroundColor: item.color,
            }}
            title={`${item.label}: ${formatPercent(item.value)}`}
          />
        ))}
      </div>
    </div>
  );
}

// Eski total sonuçlar tasarımı referans olarak tutuluyor; render artık MobilityTotalResultsV2 kullanıyor.
// eslint-disable-next-line no-unused-vars
function MobilityTotalResults({ aggregate }) {
  const share = (label) => getShareValue(aggregate, label);
  const operatorData = [
    { label: "Tcell", value: share("Kullanılan Mobil Operatör Dağılımı % Tcell") },
    { label: "TurkTelekom", value: share("Kullanılan Mobil Operatör Dağılımı % TurkTelekom") },
    { label: "Vodafone", value: share("Kullanılan Mobil Operatör Dağılımı % Vodafone") },
  ];
  const phoneData = [
    { label: "Apple", value: share("Sahip Olunan Telefon Markası Dağılımı Apple") },
    { label: "Samsung", value: share("Sahip Olunan Telefon Markası Dağılımı Samsung") },
    { label: "Xiaomi", value: share("Sahip Olunan Telefon Markası Dağılımı Xiaomi") },
  ];
  const sesData = [
    { label: "A", value: share("Sosyo Ekonomik Segment Dağılımı A") },
    { label: "B", value: share("Sosyo Ekonomik Segment Dağılımı B") },
    { label: "C", value: share("Sosyo Ekonomik Segment Dağılımı C") },
    { label: "DE", value: share("Sosyo Ekonomik Segment Dağılımı DE") },
  ];
  const cards = [
    { title: "Toplam Haftalık Erişim", value: formatNumber(aggregate.totalUsers), accent: true },
    ...operatorData.map((item) => ({ title: `Kullanılan Mobil Operatör Dağılımı % ${item.label}`, value: formatPercent(item.value) })),
    ...phoneData.map((item) => ({ title: `Sahip Olunan Telefon Markası Dağılımı ${item.label}`, value: formatPercent(item.value) })),
    ...sesData.map((item) => ({ title: `Sosyo Ekonomik Segment Dağılımı ${item.label}`, value: formatPercent(item.value) })),
    { title: "Üniversite Öğrencisi Oranı %", value: formatPercent(share("Üniversite Öğrencisi Oranı %")) },
  ];

  return (
    <div className="rounded-xl border border-pink-200 bg-pink-50/60 p-4 shadow-sm">
      <div className="mb-4">
        <h3 className="font-semibold text-pink-700">Total Sonuçlar (Eşleşen Adresler)</h3>
        <p className="mt-1 text-xs text-slate-500">Tekil kullanıcı değerleri haftalıktır; kaynak kolon: Ortalama haftalık tekil kişi(mart-agustos 2025).</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => (
          <div key={card.title} className={`rounded-xl border p-4 shadow-sm ${card.accent ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-white/90"}`}>
            <div className={`text-xs font-medium truncate ${card.accent ? "text-blue-700" : "text-slate-700"}`} title={card.title}>{card.title}</div>
            <div className="mt-3 text-xl font-bold text-slate-950">{card.value}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-6">
        <PieChart title="Mobil Operatör" data={operatorData} />
        <PieChart title="Telefon Markası" data={phoneData} />
        <PieChart title="Sosyo Ekonomik Segment" data={sesData} />
      </div>
    </div>
  );
}

function PieChart({ title, data }) {
  const visibleData = data
    .map((item, index) => ({ ...item, color: PIE_COLORS[index % PIE_COLORS.length], value: Number(item.value) || 0 }))
    .filter((item) => item.value > 0);
  const total = visibleData.reduce((sum, item) => sum + item.value, 0);

  if (!total) {
    return (
      <div className="text-center text-sm text-slate-500">
        <div className="font-semibold text-slate-900 mb-3">{title}</div>
        Veri yok
      </div>
    );
  }

  let cursor = 0;
  const segments = visibleData.map((item) => {
    const start = cursor;
    const end = cursor + (item.value / total) * 360;
    cursor = end;
    const mid = start + (end - start) / 2;
    return {
      ...item,
      start,
      end,
      path: describePieSlice(100, 100, 92, start, end),
      labelPoint: polarToCartesian(100, 100, 56, mid),
    };
  });

  return (
    <div className="flex flex-col items-center">
      <div className="font-semibold text-sm text-slate-900 mb-3">{title}</div>
      <svg className="h-[200px] w-[200px] drop-shadow-sm" viewBox="0 0 200 200" role="img" aria-label={title}>
        {segments.map((item) => (
          <path key={item.label} d={item.path} fill={item.color} />
        ))}
        {segments.map((item) => (
          <text
            key={`${item.label}-label`}
            x={item.labelPoint.x}
            y={item.labelPoint.y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-white text-[12px] font-extrabold"
            style={{ paintOrder: "stroke", stroke: "rgba(15, 23, 42, 0.45)", strokeWidth: 3, strokeLinejoin: "round" }}
          >
            {piePercent(item.value)}
          </text>
        ))}
      </svg>
      <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-2 text-xs text-slate-700">
        {visibleData.map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className="h-3 w-8 inline-block" style={{ backgroundColor: item.color }} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function piePercent(value) {
  return `${((Number(value) || 0) * 100).toLocaleString("tr-TR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians)),
  };
}

function describePieSlice(centerX, centerY, radius, startAngle, endAngle) {
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    `M ${centerX} ${centerY}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
}

function getShareValue(aggregate, label) {
  const normalizedLabel = trNormalize(label);
  const match = Object.entries(aggregate?.shares || {}).find(([key]) => trNormalize(key) === normalizedLabel);
  return Number(match?.[1]) || 0;
}

function CompactMobilityLocationRow({ site, rows, aggregate, index = 0 }) {
  const metrics = getSesMetrics(aggregate);
  const detailedShares = Object.entries(aggregate.shares || {})
    .filter(([name]) => !name.startsWith("__EMPTY"))
    .sort(([first], [second]) => first.localeCompare(second, "tr", { sensitivity: "base" }));

  return (
    <details className={`group overflow-hidden rounded-xl border border-slate-200 shadow-sm ${index % 2 === 0 ? "bg-white" : "bg-slate-200"}`}>
      <summary className="list-none cursor-pointer px-4 py-3 [&::-webkit-details-marker]:hidden">
        <div className="grid items-center gap-3 md:grid-cols-[minmax(260px,1fr)_190px_140px_minmax(280px,380px)_32px]">
          <div className="min-w-0">
            <div className="truncate text-sm font-extrabold text-slate-800" title={site.name || site.address}>
              {site.name || site.address}
            </div>
            {/* <div className="mt-1 text-xs text-slate-500">{rows.length} eşleşme</div> */}
          </div>
          <div className="text-sm font-medium text-slate-500">{site.unitType || "—"}</div>
          <div className="text-sm font-extrabold tabular-nums text-slate-900">{formatNumber(aggregate.totalUsers)}</div>
          <SesDistributionBar metrics={metrics} />
          <div className="flex items-center justify-end text-gray-600" aria-label="Detay aç/kapat">
            <svg className="h-4 w-4 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </summary>

      <div className="border-t border-slate-200 bg-slate-50 p-4">
        <LocationDetailTotalStyle aggregate={aggregate} rows={rows} metrics={metrics} />
        <div className="hidden">
        <div className="grid gap-4 lg:grid-cols-[1fr_1fr_1.2fr]">
          <CompactDonut title="Mobil Operatör" data={[
            { label: "Turkcell", value: getShareValue(aggregate, "Kullanılan Mobil Operatör Dağılımı % Tcell") },
            { label: "Türk Telekom", value: getShareValue(aggregate, "Kullanılan Mobil Operatör Dağılımı % TurkTelekom") },
            { label: "Vodafone", value: getShareValue(aggregate, "Kullanılan Mobil Operatör Dağılımı % Vodafone") },
          ]} />
          <CompactDonut title="Telefon Markası" data={[
            { label: "Apple", value: getShareValue(aggregate, "Sahip Olunan Telefon Markası Dağılımı Apple") },
            { label: "Samsung", value: getShareValue(aggregate, "Sahip Olunan Telefon Markası Dağılımı Samsung") },
            { label: "Xiaomi", value: getShareValue(aggregate, "Sahip Olunan Telefon Markası Dağılımı Xiaomi") },
            { label: "Diğer", value: getShareValue(aggregate, "Sahip Olunan Telefon Markası Dağılımı Diğer") },
          ]} />
          <div>
            <div className="mb-3 text-xs font-extrabold uppercase tracking-wide text-slate-500">Öne Çıkanlar</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <MetricCard title="Toplam Haftalık Erişim" value={formatNumber(aggregate.totalUsers)} accent="blue" />
              {metrics.map((metric) => (
                <MetricCard key={metric.key} title={`SES ${metric.label}`} value={`${formatPercent(metric.share)} share`} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {detailedShares.slice(0, 12).map(([name, value]) => (
            <div key={name} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="truncate text-xs text-slate-500" title={name}>{name}</div>
              <div className="mt-1 text-sm font-semibold text-slate-800">{formatPercent(value)}</div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </details>
  );
}

function SesDistributionBar({ metrics }) {
  const colors = ["#0f3f66", "#2f6690", "#9bc2de", "#d5e5f2"];
  const items = metrics.map((metric, index) => ({
    ...metric,
    color: colors[index],
    width: `${Math.max(metric.share * 100, metric.share > 0 ? 3 : 0)}%`,
  }));
  return (
    <div>
      <div className="mb-1 grid grid-cols-4 gap-1 text-[10px] font-extrabold text-slate-600">
        {items.map((metric) => (
          <span key={metric.key} className="text-center tabular-nums">
            {formatPercent(metric.share)}
          </span>
        ))}
      </div>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
        {items.map((metric) => (
          <span
            key={metric.key}
            title={`SES ${metric.label}: ${formatPercent(metric.share)}`}
            style={{ width: metric.width, backgroundColor: metric.color }}
          />
        ))}
      </div>
      <div className="mt-1.5 grid grid-cols-4 gap-1 text-[10px] font-semibold text-slate-500">
        {items.map((metric) => (
          <span key={metric.key} className="flex items-center justify-center gap-1">
            <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: metric.color }} />
            {metric.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function LocationDetailTotalStyle({ aggregate, rows, metrics }) {
  const share = (label) => getShareValue(aggregate, label);
  const operatorData = [
    { label: "Turkcell", value: share("Kullanılan Mobil Operatör Dağılımı % Tcell") },
    { label: "Vodafone", value: share("Kullanılan Mobil Operatör Dağılımı % Vodafone") },
    { label: "Türk Telekom", value: share("Kullanılan Mobil Operatör Dağılımı % TurkTelekom") },
  ].sort((first, second) => second.value - first.value);
  const phoneData = withOtherShare([
    { label: "Apple", value: share("Sahip Olunan Telefon Markası Dağılımı Apple") },
    { label: "Samsung", value: share("Sahip Olunan Telefon Markası Dağılımı Samsung") },
    { label: "Xiaomi", value: share("Sahip Olunan Telefon Markası Dağılımı Xiaomi") },
  ]);
  const sesData = metrics.map((metric) => ({
    label: `SES ${metric.label}`,
    value: metric.share,
  }));
  const extraShareMetrics = getExtraTotalShareMetrics(aggregate);
  const operatorGap = operatorData.length > 1 ? Math.max(0, operatorData[0].value - operatorData[1].value) : 0;
  const leadingPhone = [...phoneData].sort((first, second) => second.value - first.value)[0];
  const leadingSes = [...sesData].sort((first, second) => second.value - first.value)[0];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="grid gap-6 xl:grid-cols-[280px_1fr]">
        <div className="xl:border-r xl:border-slate-100 xl:pr-6">
          <div className="text-xs font-extrabold text-slate-500">Toplam Haftalık Erişim</div>
          <div className="mt-1 text-4xl font-black tracking-tight text-slate-950">{formatNumber(aggregate.totalUsers)}</div>
          
          {extraShareMetrics.length > 0 && (
            <div className="mt-6 space-y-4">
              {extraShareMetrics.slice(0, 4).map((metric) => (
                <TotalInlineMetric key={metric.label} label={metric.label} value={formatPercent(metric.value)} />
              ))}
            </div>
          )}
        </div>

        <div className="detail-distribution-grid grid gap-8 lg:grid-cols-3">
          <DetailDistributionList
            title="Mobil Operatör"
            data={operatorData}
            colors={["#111827", "#e60000", "#e8eef5", "#ead2da"]}
            logoType="operator"
          />
          <DetailDistributionList
            title="Telefon Markası"
            data={phoneData}
            colors={["#111827", "#e5e7eb", "#ff6900", "#f8fafc"]}
            logoType="phone"
          />
          <DetailDistributionList
            title="Sosyo-Ekonomik Segment"
            data={sesData}
            colors={["#0f3f66", "#2f6690", "#9bc2de", "#d5e5f2"]}
            logoType="ses"
          />
        </div>

        <div className="hidden">
        <div>
          <div className="mb-4 text-sm font-extrabold text-slate-800">Mobil Operatör</div>
          <div className="divide-y divide-slate-100">
            {operatorData.map((item, index) => (
              <div key={item.label} className="flex items-center gap-3 py-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-extrabold text-slate-500">{index + 1}</span>
                <span className="flex-1 text-sm font-medium text-slate-600">{item.label}</span>
                <span className="text-2xl font-black text-slate-700">{formatPercent(item.value)}</span>
              </div>
            ))}
          </div>
          {operatorGap > 0 && (
            <div className="mt-3 text-xs font-medium text-slate-400">
              Lider ile 2. arasında <span className="font-extrabold text-slate-700">+{formatPercent(operatorGap).replace("%", "")} puan</span> fark
            </div>
          )}
        </div>

        <TotalDistributionBlock
          title="Telefon Markası"
          data={phoneData}
          footer={leadingPhone ? <>En yaygın marka <b>{leadingPhone.label}</b> ({formatPercent(leadingPhone.value)})</> : null}
          colors={["#0f766e", "#2d8f7f", "#88c5ba", "#cfe8e3"]}
        />

        <TotalDistributionBlock
          title="Sosyo-Ekonomik Segment"
          data={sesData}
          footer={leadingSes ? <>Erişimin yarısından fazlası <b>{leadingSes.label}</b> segmentinde</> : null}
          colors={["#0f3f66", "#2f6690", "#9bc2de", "#d5e5f2"]}
        />
        </div>
      </div>
    </div>
  );
}

function CompactDonut({ title, data, colors = PIE_COLORS }) {
  const visibleData = withOtherShare(data)
    .map((item, index) => ({ ...item, color: colors[index % colors.length], value: Number(item.value) || 0 }))
    .filter((item) => item.value > 0);
  const total = visibleData.reduce((sum, item) => sum + item.value, 0);
  let cursor = 0;
  const segments = visibleData.map((item) => {
    const start = cursor;
    const end = cursor + (item.value / total) * 360;
    cursor = end;
    const mid = start + (end - start) / 2;
    return {
      ...item,
      path: describeDonutSlice(60, 60, 54, 24, start, end),
      labelPoint: polarToCartesian(60, 60, 39, mid),
    };
  });

  return (
    <div>
      <div className="mb-3 text-xs font-extrabold uppercase tracking-wide text-slate-500">{title}</div>
      <div className="flex items-center gap-4">
        <svg className="h-28 w-28 shrink-0 drop-shadow-sm" viewBox="0 0 120 120" role="img" aria-label={title}>
          {total ? segments.map((item) => (
            <path key={item.label} d={item.path} fill={item.color} />
          )) : <circle cx="60" cy="60" r="54" fill="#e2e8f0" />}
          {total ? segments.map((item) => (
            <text
              key={`${item.label}-label`}
              x={item.labelPoint.x}
              y={item.labelPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-white text-[9px] font-extrabold"
              style={{ paintOrder: "stroke", stroke: "rgba(15, 23, 42, 0.45)", strokeWidth: 2.4, strokeLinejoin: "round" }}
            >
              {piePercent(item.value)}
            </text>
          )) : null}
        </svg>
        <div className="min-w-0 flex-1 space-y-2 text-xs">
          {visibleData.map((item) => (
            <div key={item.label} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                {item.label}
              </span>
              <span className="font-bold text-slate-900">{formatPercent(item.value)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function withOtherShare(data) {
  const normalizedData = (data || []).map((item) => ({ ...item, value: Number(item.value) || 0 }));
  const hasOther = normalizedData.some((item) => ["diğer", "diger", "other"].includes(trNormalize(item.label)));
  const sum = normalizedData.reduce((total, item) => total + item.value, 0);
  if (!hasOther && sum > 0 && sum < 0.995) {
    return [...normalizedData, { label: "Diğer", value: Math.max(0, 1 - sum) }];
  }
  return normalizedData;
}

function describeDonutSlice(centerX, centerY, outerRadius, innerRadius, startAngle, endAngle) {
  const outerStart = polarToCartesian(centerX, centerY, outerRadius, endAngle);
  const outerEnd = polarToCartesian(centerX, centerY, outerRadius, startAngle);
  const innerStart = polarToCartesian(centerX, centerY, innerRadius, startAngle);
  const innerEnd = polarToCartesian(centerX, centerY, innerRadius, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${innerEnd.x} ${innerEnd.y}`,
    "Z",
  ].join(" ");
}

// Eski kart detay tasarımı referans olarak tutuluyor; render artık CompactMobilityLocationRow kullanıyor.
// eslint-disable-next-line no-unused-vars
function MobilityLocationRow({ site, rows, aggregate }) {
  const metrics = getSesMetrics(aggregate);
  const detailedShares = Object.entries(aggregate.shares || {})
    .filter(([name]) => !name.startsWith("__EMPTY"))
    .sort(([first], [second]) => first.localeCompare(second, "tr", { sensitivity: "base" }));
  return (
    <details className="group rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <summary className="list-none cursor-pointer p-4 [&::-webkit-details-marker]:hidden">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-slate-800 truncate" title={site.name || site.address}>{site.name || site.address}</div>
            <div className="text-xs text-slate-500 mt-1">{rows.length} eşleşme{site.unitType ? ` · ${site.unitType}` : ""}</div>
          </div>
          <div className="flex items-center gap-1 text-xs font-medium text-blue-700">
            <span>Detayları Gör</span>
            <svg className="h-4 w-4 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
          <MetricCard title="Toplam Haftalık Erişim" value={formatNumber(aggregate.totalUsers)} accent="blue" />
          {metrics.map((metric) => (
            <MetricCard key={metric.key} title={`SES ${metric.label}`} value={`${formatPercent(metric.share)} share`} />
          ))}
        </div>
      </summary>

      <div className="border-t border-slate-200 bg-slate-50/60 p-4 space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Detaylı Dağılımlar</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {detailedShares.map(([name, value]) => (
              <div key={name} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs text-slate-500 truncate" title={name}>{name}</div>
                <div className="text-sm font-semibold text-slate-800 mt-1">{formatPercent(value)}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Eşleşen Mobility Kayıtları</h4>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-[520px] w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr><th className="p-3">Adres</th><th className="p-3">Kaynak</th><th className="p-3 text-right">Haftalık Erişim</th></tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.address}-${row.__dataset}-${index}`} className="border-t border-slate-100">
                    <td className="p-3">{row.address}</td><td className="p-3">{row.__dataset || "—"}</td><td className="p-3 text-right font-medium">{formatNumber(row.users)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </details>
  );
}

function MetricCard({ title, value, secondary, accent }) {
  return (
    <div className={`rounded-lg border p-3 ${accent === "blue" ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="text-xs font-medium text-slate-600">{title}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
      {secondary && <div className="text-xs text-slate-500 mt-0.5">{secondary}</div>}
    </div>
  );
}
